import React, { useState } from 'react';
import { Box, Collapse, Grid, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UICard from 'components/ui/UICard';
import UIContainer from 'components/ui/UIContainer';
import Logo from 'assets/logo.svg?react';
import UIText from 'components/ui/UIText';
import UIFlex from 'components/ui/UIFlex';
import { Link } from 'react-router-dom';
import { pagePaths } from 'routes/paths';

interface IAppFooterProps {}

// Logo + 3 sections in one row from md (≥900px): each takes a quarter (3/12).
// On sm — brand stays full-width and the 3 sections share the next row evenly.
// On xs — stacks, each section becomes a collapsible accordion (see FooterColumn).
const brandColSize = { xs: 12, sm: 12, md: 3 };
const navColSize = { xs: 12, sm: 4, md: 3 };

// On the indigo footer we can't use primary.main for hover — it's the
// background colour and would make links disappear. Use full white as
// the active state and a softened white for resting links.
const footerLinkSx = {
  color: 'rgba(255, 255, 255, 0.78)',
  '&:hover': {
    color: 'common.white',
  },
};

const COL_HEADING_FZ = 14;
const LINK_FZ = 13;
const LINK_LINE_HEIGHT = 1.7;

interface FooterSection {
  id: string;
  title: string;
  links: Array<{ label: string; to?: string }>;
}

const SECTIONS: FooterSection[] = [
  {
    id: 'service',
    title: 'Сервіс',
    links: [
      { label: 'Як працює додаток' },
      { label: 'FAQ' },
      { label: 'Написати нам' },
      { label: 'Pricing' },
      { label: 'Privacy Policy' },
      { label: 'Terms of Service' },
    ],
  },
  {
    id: 'dictionary',
    title: 'Словник',
    links: [
      { label: 'На основі рівня', to: pagePaths.levelBased },
      { label: 'Неправильні дієслова' },
      { label: 'Найпоширеніші', to: pagePaths.mostCommon },
      { label: 'Тематичні', to: pagePaths.topicRelated },
      { label: 'Частини мови' },
    ],
  },
  {
    id: 'expressions',
    title: 'Вирази',
    links: [
      { label: 'Фразові дієслова' },
      { label: 'Практичні вирази' },
      { label: 'Словосполучення' },
      { label: 'Ідіоми' },
    ],
  },
  // Граматика — hidden until the section actually has pages to link to.
  // {
  //   id: 'grammar',
  //   title: 'Граматика',
  //   links: [
  //     { label: 'Часи' },
  //     { label: 'Речення' },
  //     { label: 'Пунктуація та правопис' },
  //   ],
  // },
];

interface FooterLinkProps {
  label: string;
  to?: string;
}

const FooterLink: React.FC<FooterLinkProps> = ({ label, to }) => {
  const text = (
    <UIText
      fz={LINK_FZ}
      sx={{ lineHeight: LINK_LINE_HEIGHT, ...(to ? footerLinkSx : undefined) }}
    >
      {label}
    </UIText>
  );
  return to ? <Link to={to}>{text}</Link> : text;
};

interface FooterColumnProps {
  section: FooterSection;
  collapsible: boolean;
}

const FooterColumn: React.FC<FooterColumnProps> = ({ section, collapsible }) => {
  const [open, setOpen] = useState(false);

  if (!collapsible) {
    return (
      <Grid size={navColSize}>
        <UIText fz={COL_HEADING_FZ} fw={700} sx={{ mb: 1 }}>
          {section.title}
        </UIText>
        {section.links.map((l) => (
          <FooterLink key={l.label} label={l.label} to={l.to} />
        ))}
      </Grid>
    );
  }

  return (
    <Grid
      size={navColSize}
      sx={{
        // Soft white separator instead of `divider` — the theme token
        // is tuned for light backgrounds and goes invisible against
        // indigo.
        borderTop: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.16)',
        py: 0.5,
      }}
    >
      <Box
        component={'button'}
        onClick={() => setOpen((v) => !v)}
        sx={{
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          font: 'inherit',
          justifyContent: 'space-between',
          p: '8px 0',
          textAlign: 'left',
          width: '100%',
        }}
      >
        <UIText fz={COL_HEADING_FZ} fw={700}>
          {section.title}
        </UIText>
        <IconButton
          size={'small'}
          component={'span'}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform .18s ease',
          }}
        >
          <ExpandMoreIcon fontSize={'small'} />
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pb: 1 }}>
          {section.links.map((l) => (
            <FooterLink key={l.label} label={l.label} to={l.to} />
          ))}
        </Box>
      </Collapse>
    </Grid>
  );
};

const AppFooter: React.FC<IAppFooterProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <UICard
      sx={{
        // Brand-indigo ground at the bottom of the page — strongest
        // visual closure of the layout. White logo + light text sit on
        // top; explicit text/link colours below override any inherited
        // dark-on-light values.
        bgcolor: 'primary.main',
        borderRadius: 0,
        boxShadow: 'none',
        color: 'common.white',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, md: 2.5 },
      }}
    >
      <UIContainer>
        <UIFlex
          alignItems={'flex-start'}
          columnSpacing={{ xs: 0, sm: 3, md: 4 }}
          rowSpacing={{ xs: 0, md: 3 }}
        >
          <Grid size={brandColSize} sx={{ pb: { xs: 1.5, sm: 0 } }}>
            <Link to={pagePaths.index}>
              <Box
                component={Logo}
                sx={{
                  // White logo on the indigo ground — keeps brand
                  // recognition strong even when the standard purple
                  // disappears into the background.
                  color: 'common.white',
                  display: 'block',
                  height: { xs: 26, md: 32 },
                  mb: 0.75,
                  width: 'auto',
                }}
              />
            </Link>
            <UIText
              fz={13}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {'Effective, structured and transparent learning.'}
            </UIText>
          </Grid>
          {SECTIONS.map((s) => (
            <FooterColumn key={s.id} section={s} collapsible={isMobile} />
          ))}
        </UIFlex>
      </UIContainer>
    </UICard>
  );
};

export default AppFooter;
